import React from "react";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import styles from "../styles/results.module.scss";

/**
 * Chip-per-file selector for multi-document runs. Each chip shows the file
 * name, an accept/reject dot and the confidence percentage; the selected
 * chip drives which verdict the per-file cards below display.
 *
 * NOTE: `verdict.reason` is the model's free-text summary and is present on
 * ACCEPTED documents too — the rejection dot keys off the actual gates
 * (AI error, doc-type gate, self-authored, HIGH contradictions) instead.
 *
 * @param {Object} props
 * @param {Object[]} [props.files] — report `request.files`
 * @param {Object[]} [props.verdicts] — report `verdicts`
 * @param {number} props.selectedIndex
 * @param {(index: number) => void} props.onSelect
 */
function BatchFileStrip({files, verdicts, selectedIndex, onSelect}) {
    const verdictList = Array.isArray(verdicts) ? verdicts : [];
    const fileList = Array.isArray(files) ? files : [];
    const count = Math.max(verdictList.length, fileList.length);
    if (count <= 1) return null;

    return (
        <div className={styles.fileStrip}>
            {Array.from({length: count}, (_, index) => {
                const verdict = verdictList[index];
                const filename = verdict?.filename || fileList[index]?.filename || `Document ${index + 1}`;
                const rejected =
                    !!verdict?.error ||
                    verdict?.doc_type_confirmed === false ||
                    verdict?.doc_type_actual === "self_authored_statement" ||
                    (Array.isArray(verdict?.fraud_signals) && verdict.fraud_signals.includes("self_authored_document")) ||
                    (Array.isArray(verdict?.contradictions) && verdict.contradictions.some(contradiction => contradiction?.severity === "high"));
                const confidence = typeof verdict?.confidence === "number" ? `${Math.round(verdict.confidence * 100)}%` : "—";

                return (
                    <button key={index} type="button" className={`${styles.fileChip} ${index === selectedIndex ? styles.fileChipSelected : ""}`} onClick={() => onSelect(index)}>
                        <span className={`${styles.fileChipDot} ${rejected ? styles.fileChipDotRejected : styles.fileChipDotAccepted}`} />
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.fileChipName}>
                            {filename}
                        </Text>
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.fileChipConfidence}>
                            {confidence}
                        </Text>
                    </button>
                );
            })}
        </div>
    );
}

export default BatchFileStrip;
